import React, { useState, useEffect, useCallback } from 'react';
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentIcon,
  MusicalNoteIcon,
  MicrophoneIcon,
  FaceSmileIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { Message, MessageListResponse } from '../types';
import { messagesApi, handleApiError } from '../utils/api';

const senderColors: Record<string, string> = {
  CLIENT: 'bg-blue-100 text-blue-800',
  AGENT: 'bg-green-100 text-green-800',
  SYSTEM: 'bg-gray-100 text-gray-800',
  client: 'bg-blue-100 text-blue-800',
  agent: 'bg-green-100 text-green-800',
  system: 'bg-gray-100 text-gray-800',
};

const typeIcons: Record<string, React.ReactNode> = {
  TEXT: <ChatBubbleLeftRightIcon className="h-4 w-4" />,
  PHOTO: <PhotoIcon className="h-4 w-4" />,
  VIDEO: <VideoCameraIcon className="h-4 w-4" />,
  DOCUMENT: <DocumentIcon className="h-4 w-4" />,
  AUDIO: <MusicalNoteIcon className="h-4 w-4" />,
  VOICE: <MicrophoneIcon className="h-4 w-4" />,
  STICKER: <FaceSmileIcon className="h-4 w-4" />,
  SYSTEM: <CogIcon className="h-4 w-4" />,
  text: <ChatBubbleLeftRightIcon className="h-4 w-4" />,
  photo: <PhotoIcon className="h-4 w-4" />,
  video: <VideoCameraIcon className="h-4 w-4" />,
  document: <DocumentIcon className="h-4 w-4" />,
  audio: <MusicalNoteIcon className="h-4 w-4" />,
  voice: <MicrophoneIcon className="h-4 w-4" />,
  sticker: <FaceSmileIcon className="h-4 w-4" />,
  system: <CogIcon className="h-4 w-4" />,
};

function formatDate(dateString: string) {
  const d = new Date(dateString);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Messages() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [sessionFilter, setSessionFilter] = useState('');
  const [senderFilter, setSenderFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await messagesApi.getAll({
        ...(sessionFilter && { session_id: sessionFilter }),
        ...(senderFilter && { sender_type: senderFilter }),
        ...(typeFilter && { message_type: typeFilter }),
        ...(searchQuery && { search: searchQuery }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        page,
        per_page: perPage,
      });
      const data: MessageListResponse = res.data;
      setMessages(data.messages ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(handleApiError(err));
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [sessionFilter, senderFilter, typeFilter, searchQuery, dateFrom, dateTo, page, perPage]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Messages
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {total} messages total
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Session ID</label>
              <input
                type="text"
                placeholder="e.g. abc123"
                value={sessionFilter}
                onChange={(e) => { setSessionFilter(e.target.value); setPage(1); }}
                className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sender</label>
              <select
                value={senderFilter}
                onChange={(e) => { setSenderFilter(e.target.value); setPage(1); }}
                className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="CLIENT">Client</option>
                <option value="AGENT">Agent</option>
                <option value="SYSTEM">System</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="TEXT">Text</option>
                <option value="PHOTO">Photo</option>
                <option value="VIDEO">Video</option>
                <option value="AUDIO">Audio</option>
                <option value="VOICE">Voice</option>
                <option value="DOCUMENT">Document</option>
                <option value="STICKER">Sticker</option>
                <option value="SYSTEM">System</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Search content</label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-9 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading messages…</div>
        ) : messages.length === 0 ? (
          <div className="p-12 text-center">
            <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No messages found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {sessionFilter || senderFilter || typeFilter || searchQuery || dateFrom || dateTo
                ? 'Try adjusting your filters.'
                : 'Messages will appear here once sessions are active.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {messages.map((msg) => (
                  <tr key={msg.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(msg.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        className="text-xs text-blue-600 hover:underline font-mono"
                        onClick={() => { setSessionFilter(msg.session_id.toString()); setShowFilters(true); setPage(1); }}
                        title="Filter by this session"
                      >
                        {msg.session_id.toString().slice(0, 12)}
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${senderColors[msg.sender_type] || 'bg-gray-100 text-gray-800'}`}>
                        {msg.sender_username || msg.sender_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center text-xs text-gray-600 gap-1">
                        {typeIcons[msg.message_type] || null}
                        {msg.message_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-md truncate" title={msg.content}>
                      {msg.content}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{(page - 1) * perPage + 1}</span> –{' '}
              <span className="font-medium">{Math.min(page * perPage, total)}</span> of{' '}
              <span className="font-medium">{total}</span>
            </div>
            <div className="flex space-x-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="inline-flex items-center px-3 text-sm text-gray-700">
                {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
